import { VerificationRequest } from 'matrix-js-sdk/src/crypto-api';
import { Dispatch, useEffect, useMemo, useReducer } from 'react';

import { VerificationErrorEnum } from '../../utils';

type State = {
  sasDecimalNumbers: string | null;
  isRequesting: boolean;
  isConfirming: boolean;
  errorKey: VerificationErrorEnum | null;
  waitingModalFlowStarted: boolean;
};

type Action =
  | { type: 'SET_SAS_DECIMAL_NUMBERS'; payload: string | null }
  | { type: 'SET_IS_REQUESTING'; payload: boolean }
  | { type: 'SET_IS_CONFIRMING'; payload: boolean }
  | { type: 'SET_ERROR_KEY'; payload: VerificationErrorEnum | null }
  | { type: 'SET_WAITING_MODAL_FLOW_STARTED'; payload: boolean }
  | { type: 'RESET' };

const initialState: State = {
  sasDecimalNumbers: null,
  isRequesting: false,
  isConfirming: false,
  errorKey: null,
  waitingModalFlowStarted: false,
};

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'SET_SAS_DECIMAL_NUMBERS':
      return { ...state, sasDecimalNumbers: action.payload };
    case 'SET_IS_REQUESTING':
      return { ...state, isRequesting: action.payload };
    case 'SET_IS_CONFIRMING':
      return { ...state, isConfirming: action.payload };
    case 'SET_ERROR_KEY':
      return { ...state, errorKey: action.payload };
    case 'SET_WAITING_MODAL_FLOW_STARTED':
      return { ...state, waitingModalFlowStarted: action.payload };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
};

const createSetters = (dispatch: Dispatch<Action>) => ({
  setSasDecimalNumbers: (payload: string | null) => dispatch({ type: 'SET_SAS_DECIMAL_NUMBERS', payload }),
  setIsRequesting: (payload: boolean) => dispatch({ type: 'SET_IS_REQUESTING', payload }),
  setIsConfirming: (payload: boolean) => dispatch({ type: 'SET_IS_CONFIRMING', payload }),
  setErrorKey: (payload: VerificationErrorEnum | null) => dispatch({ type: 'SET_ERROR_KEY', payload }),
  setWaitingModalFlowStarted: (payload: boolean) => dispatch({ type: 'SET_WAITING_MODAL_FLOW_STARTED', payload }),
  resetControllerState: () => dispatch({ type: 'RESET' }),
});

const useSasVerificationState = (activeRequest: VerificationRequest | null) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const setters = useMemo(() => createSetters(dispatch), []);

  useEffect(() => {
    if (!activeRequest) dispatch({ type: 'RESET' });
  }, [activeRequest]);

  return { ...state, ...setters };
};

export default useSasVerificationState;
